/**
 * Payment Telemetry System
 *
 * Simplified payment logging solution without OpenTelemetry.
 * Maintains the interface for compatibility but logs to console or no-op.
 */

// Payment-specific semantic conventions
export const PAYMENT_ATTRIBUTES = {
  // Payment Operation Attributes
  PAYMENT_EVENT_TYPE: 'payment.event.type',
  PAYMENT_CORRELATION_ID: 'payment.correlation.id',
  PAYMENT_ATTEMPT_NUMBER: 'payment.attempt.number',
  PAYMENT_PROCESSOR: 'payment.processor',
  PAYMENT_METHOD: 'payment.method',
  PAYMENT_AMOUNT: 'payment.amount',
  PAYMENT_CURRENCY: 'payment.currency',
  PAYMENT_STATUS: 'payment.status',

  // Error Classification Attributes
  PAYMENT_ERROR_CATEGORY: 'payment.error.category',
  PAYMENT_ERROR_CODE: 'payment.error.code',
  PAYMENT_ERROR_MESSAGE: 'payment.error.message',
  PAYMENT_ERROR_RECOVERABLE: 'payment.error.recoverable',

  // Context Attributes (PCI-compliant)
  PAYMENT_MERCHANT_ID: 'payment.merchant.id',
  PAYMENT_TRANSACTION_ID: 'payment.transaction.id',
  PAYMENT_SESSION_ID: 'payment.session.id',
  PAYMENT_USER_ID: 'payment.user.id',
  PAYMENT_CUSTOMER_TYPE: 'payment.customer.type',

  // Request/Response Context (safe attributes only)
  PAYMENT_REQUEST_ID: 'payment.request.id',
  PAYMENT_RESPONSE_TIME_MS: 'payment.response.time_ms',
  PAYMENT_HTTP_STATUS: 'payment.http.status',
  PAYMENT_API_VERSION: 'payment.api.version',

  // ProPay-specific attributes
  PROPAY_ACCOUNT_ID: 'propay.account.id',
  PROPAY_SOURCE_EMAIL: 'propay.source.email',
  PROPAY_ENROLLMENT_STEP: 'propay.enrollment.step',
  PROPAY_ERROR_TYPE: 'propay.error.type',
} as const

// Payment event types
export type PaymentEventType =
  | 'payment.attempt'
  | 'payment.success'
  | 'payment.error'
  | 'payment.retry'
  | 'payment.validation.error'
  | 'payment.timeout'
  | 'payment.cancelled'
  | 'payment.refund.attempt'
  | 'payment.refund.success'
  | 'payment.refund.error'
  | 'propay.enrollment.start'
  | 'propay.enrollment.error'
  | 'propay.enrollment.success'
  | 'payment.system.initialized'
  | 'payment.abandoned'
  | 'payment.page.loaded'
  | 'payment.checkout.viewed'

// Payment error categories
export type PaymentErrorCategory =
  | 'network'
  | 'validation'
  | 'processor'
  | 'timeout'
  | 'authentication'
  | 'authorization'
  | 'configuration'
  | 'business_rule'
  | 'system'
  | 'user_cancelled'

// Payment method types
export type PaymentMethodType =
  | 'credit_card'
  | 'ach'
  | 'bank_transfer'
  | 'digital_wallet'
  | 'unknown'
  | 'unhandled_error'
  | 'unhandled_promise_rejection'

// PCI-compliant payment context (no sensitive data)
export interface PaymentContext {
  correlationId: string
  attemptNumber?: number
  merchantId?: string
  transactionId?: string
  sessionId?: string
  userId?: string
  customerType?: string

  // Payment details (safe attributes only)
  processor: string
  method: PaymentMethodType
  amount?: number
  currency?: string

  // Request context
  requestId?: string
  apiVersion?: string

  // ProPay-specific context
  propayAccountId?: string
  propaySourceEmail?: string
  propayEnrollmentStep?: string
}

// Payment error details with categorization
export interface PaymentError {
  category: PaymentErrorCategory
  code?: string
  message: string
  recoverable: boolean
  httpStatus?: number
  responseTimeMs?: number

  // Original error context (sanitized)
  originalError?: {
    name: string
    message: string
    stack?: string
    cause?: unknown
  }

  // ProPay-specific error details
  propayErrorType?: string
}

// Main payment telemetry interface
export interface PaymentTelemetry {
  logPaymentEvent(
    eventType: PaymentEventType,
    context: PaymentContext,
    error?: PaymentError,
    additionalAttributes?: Record<string, string | number | boolean>,
  ): void

  createPaymentSpan<T>(operationName: string, context: PaymentContext, operation: () => Promise<T>): Promise<T>

  logPaymentError(
    error: PaymentError,
    context: PaymentContext,
    additionalAttributes?: Record<string, string | number | boolean>,
  ): void

  generateCorrelationId(): string
}

class PaymentTelemetryImpl implements PaymentTelemetry {
  /**
   * Log structured payment events
   */
  logPaymentEvent(
    eventType: PaymentEventType,
    paymentContext: PaymentContext,
    error?: PaymentError,
    additionalAttributes: Record<string, string | number | boolean> = {},
  ): void {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Payment Telemetry] ${eventType}`, {
        context: paymentContext,
        error,
        additionalAttributes,
      })
    }
  }

  /**
   * Create payment operation span
   */
  async createPaymentSpan<T>(
    operationName: string,
    paymentContext: PaymentContext,
    operation: () => Promise<T>,
  ): Promise<T> {
    const startTime = Date.now()
    try {
      const result = await operation()
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Payment Telemetry] Span ${operationName} succeeded in ${Date.now() - startTime}ms`)
      }
      return result
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`[Payment Telemetry] Span ${operationName} failed in ${Date.now() - startTime}ms`, error)
      }
      throw error
    }
  }

  /**
   * Log payment-specific errors
   */
  logPaymentError(
    error: PaymentError,
    context: PaymentContext,
    additionalAttributes: Record<string, string | number | boolean> = {},
  ): void {
    this.logPaymentEvent('payment.error', context, error, additionalAttributes)
  }

  /**
   * Generate unique correlation ID
   */
  generateCorrelationId(): string {
    return `pay_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
}

// Export singleton instance
export const paymentTelemetry: PaymentTelemetry = new PaymentTelemetryImpl()
